import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Calculate from '@mui/icons-material/Calculate';
import AccessTime from '@mui/icons-material/AccessTime';
import ArticleIcon from '@mui/icons-material/Article';
import postsNl from '../data/posts/index';
import postsEn from '../data/posts/index.en';
import { useLanguage } from '../context/LanguageContext';
import { useT } from '../translations';

const BlogPostPage = () => {
	const { slug } = useParams();
	const navigate = useNavigate();
	const { language } = useLanguage();
	const t = useT(language);
	const posts = language === 'en' ? postsEn : postsNl;
	const post = posts.find((p) => p.slug === slug);

	if (!post) {
		return (
			<Box sx={{ textAlign: 'center', py: 8 }}>
				<Typography variant="h5" gutterBottom>
					{t('articleNotFound')}
				</Typography>
				<Button onClick={() => navigate('/blog')} startIcon={<ArrowBack />}>
					{t('backToBlog')}
				</Button>
			</Box>
		);
	}

	const formattedDate = new Date(post.date).toLocaleDateString(t('dateLocale'), {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	});

	const articleJsonLd = {
		'@context': 'https://schema.org',
		'@type': 'Article',
		headline: post.title,
		description: post.description,
		datePublished: post.date,
		url: `https://www.visualiseermijnpensioen.nl/blog/${post.slug}`,
		inLanguage: language === 'en' ? 'en' : 'nl-NL',
		publisher: {
			'@type': 'Organization',
			name: 'VisualiseermijnPensioen.nl',
			url: 'https://www.visualiseermijnpensioen.nl',
		},
	};

	const faqJsonLd = post.faq ? {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: post.faq.map((item) => ({
			'@type': 'Question',
			name: item.q,
			acceptedAnswer: { '@type': 'Answer', text: item.a },
		})),
	} : null;

	const jsonLd = faqJsonLd ? [articleJsonLd, faqJsonLd] : articleJsonLd;

	const relatedPostObjects = (post.relatedPosts || [])
		.map((slug) => posts.find((p) => p.slug === slug))
		.filter(Boolean);

	return (
		<Box sx={{ py: 4 }}>
			<SEOHead
				title={`${post.title} | VisualiseermijnPensioen.nl`}
				description={post.description}
				canonical={`https://www.visualiseermijnpensioen.nl/blog/${post.slug}`}
				lang={language === 'en' ? 'en' : 'nl'}
				jsonLd={jsonLd}
			/>
			<Button
				startIcon={<ArrowBack />}
				onClick={() => navigate('/blog')}
				sx={{ mb: 3, textTransform: 'none' }}
				color="inherit"
			>
				{t('allArticles')}
			</Button>

			{/* Header */}
			<Box sx={{ mb: 4 }}>
				<Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
					{post.tags.map((tag) => (
						<Chip key={tag} label={tag} size="small" />
					))}
				</Box>

				<Typography variant="h3" fontWeight="bold" gutterBottom lineHeight={1.2}>
					{post.title}
				</Typography>

				<Typography variant="body1" color="text.secondary" sx={{ mb: 2, fontSize: 18 }}>
					{post.description}
				</Typography>

				<Box sx={{ display: 'flex', gap: 2, color: 'text.disabled', alignItems: 'center' }}>
					<Typography variant="caption">{formattedDate}</Typography>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
						<AccessTime sx={{ fontSize: 14 }} />
						<Typography variant="caption">{post.readTime} {t('readTime')}</Typography>
					</Box>
				</Box>
			</Box>

			<Divider sx={{ mb: 4 }} />

			{/* Article content */}
			{post.sections.map((section, i) => (
				<Box key={i} sx={{ mb: 4 }}>
					<Typography variant="h5" fontWeight="bold" gutterBottom>
						{section.heading}
					</Typography>
					<Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.9 }}>
						{section.body}
					</Typography>
				</Box>
			))}

			<Divider sx={{ my: 4 }} />

			{/* Related posts */}
			{relatedPostObjects.length > 0 && (
				<Box sx={{ mb: 4 }}>
					<Typography variant="h6" fontWeight="bold" gutterBottom>
						{language === 'en' ? 'Related articles' : 'Lees ook'}
					</Typography>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
						{relatedPostObjects.map((related) => (
							<Paper
								key={related.slug}
								elevation={0}
								onClick={() => navigate(`/blog/${related.slug}`)}
								sx={{
									p: 2,
									borderRadius: 2,
									border: '1px solid',
									borderColor: 'divider',
									cursor: 'pointer',
									display: 'flex',
									alignItems: 'center',
									gap: 1.5,
									'&:hover': { bgcolor: '#f8fafc', borderColor: 'primary.light' },
									transition: 'all 0.15s',
								}}
							>
								<ArticleIcon sx={{ color: 'primary.main', flexShrink: 0, fontSize: 20 }} />
								<Box>
									<Typography variant="body2" fontWeight={600}>{related.title}</Typography>
									<Typography variant="caption" color="text.secondary">{related.description}</Typography>
								</Box>
							</Paper>
						))}
					</Box>
				</Box>
			)}

			<Divider sx={{ my: 4 }} />

			{/* CTA */}
			<Paper
				elevation={0}
				sx={{
					p: 3,
					borderRadius: 3,
					bgcolor: '#eef2ff',
					border: '1px solid',
					borderColor: 'primary.light',
					display: 'flex',
					flexDirection: { xs: 'column', md: 'row' },
					alignItems: { md: 'center' },
					gap: 2,
				}}
			>
				<Box sx={{ flex: 1 }}>
					<Typography variant="subtitle1" fontWeight="bold" gutterBottom>
						{t('blogCTATitle')}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{t('blogCTABody')}
					</Typography>
				</Box>
				<Button
					variant="contained"
					startIcon={<Calculate />}
					onClick={() => navigate('/')}
					sx={{ whiteSpace: 'nowrap', textTransform: 'none' }}
				>
					{t('blogCTAButton')}
				</Button>
			</Paper>
		</Box>
	);
};

export default BlogPostPage;
